import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FacilityService } from '../../../../service/facility-service/facility.service';

@Component({
  selector: 'app-facility-detail-modal',
  templateUrl: './facility-detail-modal.component.html',
  styleUrls: ['./facility-detail-modal.component.css']
})
export class FacilityDetailModalComponent implements OnInit, OnDestroy {
  @Input() facilityId: number | string;
  @Input() facilityCard: any;
  @Input() proposal: any;
  @Input() isSubmitting: boolean = false;
  
  @Output() onCloseModal = new EventEmitter<void>();
  @Output() onWantFacility = new EventEmitter<any>();

  facility: any = null;
  isLoading: boolean = true;
  errorMessage: string = '';
  activeImageUrl: string = '';

  mappedAmenities: any[] = [];
  isAmenitiesLoading: boolean = false;

  constructor(private facilityService: FacilityService) { }

  ngOnInit(): void {
    this.fetchFacilityDetails();
    
    const facilityTypeId = this.facilityCard 
      ? (this.facilityCard.facilitytype_id || this.facilityCard.facility_type_id)
      : null;
    if (facilityTypeId) {
      this.fetchAmenities(facilityTypeId);
    }
  }

  ngOnDestroy(): void { }

  closeModal(): void {
    this.onCloseModal.emit();
  }

  fetchFacilityDetails(): void {
    if (!this.facilityId) {
      this.isLoading = false;
      this.errorMessage = 'Facility ID is missing.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    this.facilityService.getFacilityDetails(this.facilityId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (res) => {
          this.isLoading = false;
          const data = (res && res.data) ? res.data : (res && res.facility ? res.facility : res);
          if (data) {
            this.processFacilityData(data);
          } else {
            this.errorMessage = 'No facility details found.';
          }
        },
        (error) => {
          console.error('Error fetching facility details:', error);
          this.isLoading = false;
          this.errorMessage = 'Failed to load facility details.';
        }
      );
  }

  processFacilityData(data: any): void {
    this.facility = data;
    const media = data.e_facility_media || [];
    this.activeImageUrl = media[0] && media[0].image 
      ? media[0].image 
      : 'assets/images/placeholders/facility-placeholder.jpg';

    // Fallback if type ID wasn't available inside facilityCard input
    if (this.mappedAmenities.length === 0) {
      const facilityTypeId = data.facilitytype_id || data.facility_type_id || (data.e_facility_type && data.e_facility_type.id) || (data.e_facility_types && data.e_facility_types.id);
      if (facilityTypeId) {
        this.fetchAmenities(facilityTypeId);
      }
    }
  }

  fetchAmenities(facilityTypeId: number | string): void {
    if (!facilityTypeId || !this.facilityId || this.isAmenitiesLoading || this.mappedAmenities.length > 0) {
      return;
    }

    this.isAmenitiesLoading = true;
    this.facilityService.getAllAmenities(facilityTypeId)
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(
        (allRes) => {
          const allAmenities = (allRes && allRes.data) ? allRes.data : (allRes && allRes.amenities ? allRes.amenities : allRes) || [];
          
          this.facilityService.getFacilityAmenities(this.facilityId)
            .pipe(takeUntil(componentDestroyed(this)))
            .subscribe(
              (assignedRes) => {
                this.isAmenitiesLoading = false;
                this.mapAndDisplayAmenities(allAmenities, assignedRes);
              },
              (error) => {
                console.error('Error fetching facility amenities:', error);
                this.isAmenitiesLoading = false;
                this.mapAndDisplayAmenities(allAmenities, null);
              }
            );
        },
        (error) => {
          console.error('Error fetching all amenities:', error);
          this.isAmenitiesLoading = false;
        }
      );
  }

  mapAndDisplayAmenities(allAmenities: any[], assignedRes: any): void {
    let amenitiesFacility: any[] = [];
    if (assignedRes) {
      if (Array.isArray(assignedRes.e_amenitiesfacility)) {
        amenitiesFacility = assignedRes.e_amenitiesfacility;
      } else if (assignedRes.data && Array.isArray(assignedRes.data.e_amenitiesfacility)) {
        amenitiesFacility = assignedRes.data.e_amenitiesfacility;
      } else if (Array.isArray(assignedRes.data)) {
        amenitiesFacility = assignedRes.data;
      } else if (Array.isArray(assignedRes)) {
        amenitiesFacility = assignedRes;
      }
    }

    const amenityIds = new Set();
    amenitiesFacility.forEach((a: any) => {
      const id = a.amenities_id || a.amenity_id || a.id;
      if (id !== undefined && id !== null) {
        amenityIds.add(Number(id));
      }
    });

    this.mappedAmenities = (allAmenities || [])
      .filter((a: any) => {
        const id = a.id || a.amenity_id || a.amenities_id;
        return id !== undefined && id !== null && amenityIds.has(Number(id));
      })
      .map((a: any) => {
        const name = a.name || (a.e_amenities && a.e_amenities.name) || a.label || '';
        const rawIcon = a.icon || (a.e_amenities && a.e_amenities.icon) || null;
        let isImageIcon = false;
        if (rawIcon) {
          isImageIcon = rawIcon.indexOf('/') !== -1 || rawIcon.indexOf('http') === 0;
        }
        return {
          name: name.trim(),
          icon: rawIcon,
          isImageIcon: isImageIcon
        };
      });
  }

  setActiveImage(url: string): void {
    if (url) {
      this.activeImageUrl = url;
    }
  }

  triggerWantFacility(): void {
    this.onWantFacility.emit(this.facilityCard);
  }
}
